import { useState } from '#app/composables/state';
import { redirect } from '#layers/access/public';
import { delay, TRANSITION_DURATION } from '#layers/base/public';

export function useAuthTransition() {
	const isExpanding = useState('auth-expanding', () => false);

	function triggerExpand() {
		isExpanding.value = true;
	}

	function collapse() {
		isExpanding.value = false;
	}

	return { isExpanding, triggerExpand, collapse };
}

/**
 * Hand off to the browser navigator without any transition. Used for the SSO
 * start legs (`startSsoLogin` / `startSsoSignup`), where we're redirecting to
 * the IdP and the outcome isn't known yet — the expand belongs on confirmed
 * success — and for the link flow under the default layout, where the expand
 * animation has nothing to mount against.
 *
 * `replace: true` swaps the current history entry instead of pushing, dropping
 * the originating auth page from the back stack (used by `startSsoLogin` so a
 * post-login Back skips the login page). The link flow keeps the default push
 * so its in-app originating page stays reachable via Back.
 */
/**
 * Run the expand transition, wait for it to complete, then hand off to the
 * browser navigator. The flag stays set so the panel covers the page until the
 * next document replaces it; the auth layout resets it when the browser restores
 * this page from its back/forward cache. The caller
 * is the login-success path in `pages/auth/login.vue`, where success is already
 * confirmed and we're entering the app. The SSO `auth=success` callback skips
 * it: that page is only a spinner in front of the app shell's loading screen.
 *
 * The full page load is deliberate. A session that expires in place only resets
 * the auth store, so the login page can share a document with the previous
 * user's cached state; reloading discards it (see `use-session-end.ts`). Hands
 * off with `replace` so the confirmed-success auth page leaves the back stack.
 */
export async function expandAndRedirect(url: string) {
	const transition = useAuthTransition();
	transition.triggerExpand();
	await delay(TRANSITION_DURATION.BASE);
	redirect(url, { replace: true });
}
